/**
 * Every word inside an English diagnostics question card.
 *
 * A DELIBERATE COPY of ENG_DIAG_QUESTION_COPY in the student app
 * (student-frontend/src/constants/diagnostics/eng-diag.constants.ts). The question view itself
 * is already forked between the two repos - the tutor app renders it inside a preview modal so
 * a tutor sees what a child will see - and wording that drifted between them would mean the
 * preview stopped being a preview. Keep the two in step; `diff` them when either changes.
 *
 * Two rules the wording follows, both set by the question view:
 *
 *   Tapping comes first in every sentence. Dragging does not work on a touch screen in every
 *   browser, and tapping is what a child tries anyway, so no instruction here asks for a drag
 *   without offering a tap.
 *
 *   Every instruction says how to UNDO. "Students can't change their answers" was the first
 *   thing the curriculum lead reported about these screens.
 */
export const ENG_DIAG_QUESTION_COPY = Object.freeze({
  // --- shared ------------------------------------------------------------
  WORD_BANK: "Word bank",
  TYPE_YOUR_ANSWER: "Type your answer",
  TYPE_HERE: "Type your answer here",
  BLANK_LABEL: "Blank",
  YOUR_ANSWER: "Your answer",

  // --- the worked-example types (Skill Builders only) --------------------
  // Kept word for word with the student app's ENG_DIAG_QUESTION_COPY: a tutor previewing a
  // question has to read what the child reads.
  WORKED_LABEL: "Worked example",
  WORKED_ANSWER: "Answer",
  YOUR_TURN: "Your turn",
  WRITE_HERE: "Write your answer here",

  // --- spelling_drop / punctuation_drop / column_match -------------------
  DROP_HINT:
    "Tap a word, then tap where it goes - or drag it there. Tap the cross to empty a space again.",
  DROP_EMPTY: "Drop here",
  DROP_ARMED: "Tap to place here",

  // --- image_sort --------------------------------------------------------
  SORT_HINT:
    "Tap a card, then tap the group it belongs in - or drag it there. Tap a card inside a group to take it back out.",
  SORT_POOL_LABEL: "Cards to sort",
  SORT_POOL_EMPTY: "Every card is sorted. Tap one inside a group to move it.",
  SORT_BIN_EMPTY: "Drop cards here",

  // --- mc_cloze ----------------------------------------------------------
  // "Blank", not "Gap": the `blank` type already numbers its rows "Blank 1", "Blank 2", so a
  // child met both words for the same thing on consecutive questions. The curriculum lead
  // picked this one. Kept in step with the student app, which made the same change.
  GAP_LABEL: "Blank",

  // --- punctuation_insert ------------------------------------------------
  MARKS_LABEL: "Punctuation marks",
  INSERT_HINT: "Tap a mark, then tap an insertion point (‸) in the sentence.",

  // --- text_select -------------------------------------------------------
  SELECT_HINT: "Tap in the sentence to choose. Tap again to unselect.",
  SELECT_COUNT_SUFFIX: "selected",

  // --- sentence_rebuild --------------------------------------------------
  REBUILD_HINT:
    "Tap or drag the words into the correct order. Tap a word in your sentence to take it back out.",
  REBUILD_LABEL: "Your sentence",
  REBUILD_EMPTY: "Build your sentence here",
  REBUILD_RESET: "Start again",

  // --- the four types with nothing to answer -----------------------------
  // Each says plainly that there is nothing to type, because an empty submission is marked
  // wrong everywhere else and a child who has learnt that will sit waiting for a box to fill.
  READ_WORDS_TITLE: "Read these out loud",
  READ_WORDS_BODY:
    "Your tutor is listening, so there is nothing to type. Press Submit when you have read them all.",
  PHONEMES_TITLE: "Say each sound out loud",
  PHONEMES_BODY:
    "Break each word into its sounds for your tutor. There is nothing to type - press Submit when you are done.",
  FLASHCARD_TITLE: "Read each card out loud",
  FLASHCARD_BODY:
    "Your tutor will show you these one at a time. Press Submit when you have finished.",
  DISPLAY_TITLE: "Have a look at this",
  DISPLAY_BODY:
    "There is nothing to answer here. Press Submit when you are ready to move on.",
})
